import { AlertTriangle, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button as ShButton,
} from '../../../components/shadcn/index.js';
import { Button, CopyButton } from '../../../components/ui/index.js';

import { buildPostmanCollection, downloadJson, OTP_BASE_URL, OTP_DOCS_URL } from './OtpShared.js';

export interface OtpKeyRevealDialogProps {
  /** The freshly minted key — null keeps the dialog closed. */
  apiKey: string | null;
  /** Client display name for the title. */
  clientName: string;
  onDone: () => void;
}

/**
 * One-time API-key reveal after create / rotate. The key never comes back from
 * the API again, so the dialog ignores outside-click/Escape dismissal — the
 * only way out is the explicit Done button, after the operator had every
 * chance to copy the key or the full onboarding snippet.
 */
export function OtpKeyRevealDialog({ apiKey, clientName, onDone }: OtpKeyRevealDialogProps): JSX.Element {
  const { t } = useTranslation('platform');

  // Plain-text hand-off block — deliberately untranslated: it is pasted into a
  // developer channel, like the curl examples on the docs page.
  const snippet = [
    `Base URL: ${OTP_BASE_URL}`,
    `API key:  ${apiKey ?? ''}`,
    `Docs:     ${OTP_DOCS_URL}`,
  ].join('\n');

  return (
    <Dialog open={apiKey !== null} onOpenChange={() => undefined}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('otpService.reveal.title', { defaultValue: 'API key for {{name}}', name: clientName })}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-start gap-2 rounded-md border border-danger/40 bg-danger/10 p-3 text-xs text-fg">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-danger" aria-hidden />
          <span>
            {t('otpService.reveal.warning', {
              defaultValue:
                'Copy this key NOW — it is shown only once. It cannot be recovered later, only rotated.',
            })}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <code
            dir="ltr"
            className="min-w-0 flex-1 select-all break-all rounded-md border border-border bg-bg-subtle px-3 py-2 font-mono text-sm"
          >
            {apiKey ?? ''}
          </code>
          <CopyButton value={apiKey ?? ''} toast variant="secondary" size="md" subject={t('otpService.reveal.copyKey', { defaultValue: 'API key' })} />
        </div>

        <div className="rounded-md border border-border-subtle bg-bg-subtle/40 p-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
                {t('otpService.reveal.snippetTitle', { defaultValue: 'Onboarding snippet' })}
              </div>
              <p className="mt-0.5 text-xs text-fg-muted">
                {t('otpService.reveal.snippetHint', {
                  defaultValue: 'Send this to the customer — everything they need to start.',
                })}
              </p>
            </div>
            <CopyButton
              value={snippet}
              toast
              variant="secondary"
              label={t('otpService.reveal.copySnippet', { defaultValue: 'Copy snippet' })}
              subject={t('otpService.reveal.snippetTitle', { defaultValue: 'Onboarding snippet' })}
            />
          </div>
          <pre dir="ltr" className="mt-2 overflow-x-auto rounded-md border border-border bg-bg p-3 text-start font-mono text-xs">
            {snippet}
          </pre>
        </div>

        <DialogFooter className="sm:justify-between">
          <ShButton
            variant="outline"
            size="sm"
            onClick={() => { if (apiKey) downloadJson('vylo-otp.postman_collection.json', buildPostmanCollection(apiKey)); }}
          >
            <Download className="h-4 w-4" />
            {t('otpService.reveal.postman', { defaultValue: 'Download Postman collection' })}
          </ShButton>
          <Button onClick={onDone}>
            {t('otpService.reveal.done', { defaultValue: 'Done' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
